/**
 * Customer-account API client. Parallel to lib/api.js (which carries the
 * STAFF token) — customer sessions live under their own key, persist in
 * localStorage (30-day tokens), and never mix with admin auth.
 */

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
const TOKEN_KEY = 'lookslab_customer_token'

export function getCustomerToken() {
  try { return localStorage.getItem(TOKEN_KEY) } catch { return null }
}

export function setCustomerToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch { /* ignore */ }
}

async function customerFetch(path, options = {}) {
  const token = getCustomerToken()
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    const error = new Error(body.error ?? 'Request failed')
    error.status = res.status
    throw error
  }
  return res.json()
}

export const customerApi = {
  // ── Auth ──────────────────────────────────────────────────
  // acceptedTerms is re-checked server-side; the form can't be the only gate.
  register: (email, password, firstName, lastName, marketingOptIn = false, acceptedTerms = false) =>
    customerFetch('/api/account/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, firstName, lastName, marketingOptIn, acceptedTerms, refCode: takeReferralCode() }),
    }),

  login: (email, password) =>
    customerFetch('/api/account/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  logout: () =>
    customerFetch('/api/account/logout', { method: 'POST' }),

  requestMagicLink: (email) =>
    customerFetch('/api/account/magic-link', { method: 'POST', body: JSON.stringify({ email }) }),

  requestPasswordReset: (email) =>
    customerFetch('/api/account/reset-request', { method: 'POST', body: JSON.stringify({ email }) }),

  // Consumes verify / magic / reset tokens (password only for reset)
  confirmToken: (token, password = undefined) =>
    customerFetch('/api/account/confirm', { method: 'POST', body: JSON.stringify({ token, password }) }),

  resendVerification: () =>
    customerFetch('/api/account/resend-verification', { method: 'POST' }),

  // ── Profile / orders / addresses ──────────────────────────
  me: () =>
    customerFetch('/api/account/me'),

  updateMe: (data) =>
    customerFetch('/api/account/me', { method: 'PATCH', body: JSON.stringify(data) }),

  deleteAccount: (data) =>
    customerFetch('/api/account/me', { method: 'DELETE', body: JSON.stringify(data ?? {}) }),

  myOrders: () =>
    customerFetch('/api/account/orders'),

  getAddresses: () =>
    customerFetch('/api/account/addresses'),

  addAddress: (data) =>
    customerFetch('/api/account/addresses', { method: 'POST', body: JSON.stringify(data) }),

  updateAddress: (id, data) =>
    customerFetch(`/api/account/addresses/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteAddress: (id) =>
    customerFetch(`/api/account/addresses/${id}`, { method: 'DELETE' }),

  // Checkout uses this instead of api.createOrder so the customer token
  // rides along and the order lands on the account automatically.
  createOrder: (order) =>
    customerFetch('/api/orders', { method: 'POST', body: JSON.stringify(order) }),

  // ── Rewards / referrals / wishlist ─────────────────────────
  getRewards: () =>
    customerFetch('/api/account/rewards'),

  getReferrals: () =>
    customerFetch('/api/account/referrals'),

  getReferralPayouts: () =>
    customerFetch('/api/account/referrals/payouts'),

  requestReferralPayout: (data) =>
    customerFetch('/api/account/referrals/payouts', { method: 'POST', body: JSON.stringify(data) }),

  getWishlist: () =>
    customerFetch('/api/account/wishlist'),

  addToWishlist: (productId, variantId) =>
    customerFetch('/api/account/wishlist', { method: 'POST', body: JSON.stringify({ productId, variantId }) }),

  removeFromWishlist: (id) =>
    customerFetch(`/api/account/wishlist/${id}`, { method: 'DELETE' }),
}

// Referral attribution: /r/CODE stores the code here; registration sends it.
const REF_KEY = 'lookslab_ref'
export function storeReferralCode(code) {
  try { localStorage.setItem(REF_KEY, String(code).toUpperCase()) } catch { /* ignore */ }
}
export function takeReferralCode() {
  try {
    const code = localStorage.getItem(REF_KEY)
    return code || null
  } catch { return null }
}
export function clearReferralCode() {
  try { localStorage.removeItem(REF_KEY) } catch { /* ignore */ }
}
